import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import img1 from '../../assets/marketplaces2.png';
import img2 from '../../assets/marketplaces.png';
import './ServicePage.css';

const SEO = {
  title: 'Blinkit Seller Management Services | Mad Marketing Club',
  description: 'Blinkit quick commerce management — seller onboarding, catalog setup, inventory planning, dark store optimization & q-commerce sales growth. Get started today.',
  canonical: 'https://madmarketingclub.com/services/blinkit',
  breadcrumb: [
    { name: 'Home', url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'Blinkit', url: 'https://madmarketingclub.com/services/blinkit' },
  ],
  service: { name: 'Blinkit Seller Management Services', type: 'Blinkit Quick Commerce Management' },
};

const OVERVIEW_POINTS = [
  'Access high-frequency quick commerce buyers',
  'Reach customers through rapid delivery infrastructure',
  'Sell across grocery, FMCG, beauty, wellness, and essentials categories',
  'Improve product discoverability in quick commerce search',
  'Participate in high-conversion promotional campaigns',
  'Increase repeat customer purchases',
  'Scale hyperlocal product visibility',
  'Expand through fast-growing quick commerce ecosystem',
];

const APPROACH = [
  'Blinkit seller onboarding',
  'Product catalog optimization',
  'Quick commerce SEO',
  'Inventory forecasting',
  'Dark store readiness planning',
  'Pricing optimization',
  'Campaign participation',
  'Product visibility enhancement',
  'Operational performance monitoring',
  'Revenue growth strategies',
];

const PROCESS = [
  {
    num: '01',
    title: 'Brand Consultation & Discovery',
    intro: 'We begin by understanding:',
    points: [
      'Product categories',
      'Inventory capacity',
      'Fulfillment readiness',
      'Operational structure',
      'Competitive landscape',
      'Growth objectives',
    ],
  },
  {
    num: '02',
    title: 'Blinkit Marketplace Audit',
    intro: 'We evaluate:',
    points: [
      'Product listings',
      'Catalog quality',
      'Product visibility',
      'Pricing competitiveness',
      'Inventory performance',
      'Fulfillment readiness',
      'Marketplace positioning',
    ],
  },
  {
    num: '03',
    title: 'Marketplace Growth Strategy',
    intro: 'Our customized roadmap includes:',
    points: [
      'Catalog optimization strategy',
      'Visibility enhancement planning',
      'Inventory forecasting',
      'Quick commerce pricing strategy',
      'Promotional campaign planning',
      'Operational optimization',
    ],
  },
  {
    num: '04',
    title: 'Execution & Optimization',
    intro: 'We implement:',
    points: [
      'Product listings',
      'Catalog improvements',
      'Visibility optimization',
      'Pricing updates',
      'Inventory planning',
      'Campaign enhancements',
      'Operational improvements',
    ],
  },
  {
    num: '05',
    title: 'Reporting & Scaling',
    intro: 'Continuous monitoring includes:',
    points: [
      'Sales growth',
      'Product visibility',
      'Inventory health',
      'Conversion performance',
      'Campaign effectiveness',
      'Operational efficiency',
      'Marketplace growth opportunities',
    ],
  },
];

const SERVICES = [
  {
    title: 'Blinkit Seller Account Setup & Onboarding',
    gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    points: [
      'Seller onboarding support',
      'Product category setup',
      'Business verification assistance',
      'Marketplace documentation guidance',
      'Initial account configuration',
      'Operational readiness support',
      'Product approval assistance',
    ],
  },
  {
    title: 'Product Listing & Catalog Management',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    points: [
      'Product listing creation',
      'Catalog uploads',
      'Product attribute optimization',
      'SKU structuring',
      'Product variation setup',
      'Catalog issue resolution',
      'Product data enhancement',
      'Quick commerce catalog optimization',
    ],
  },
  {
    title: 'Quick Commerce SEO Services',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    points: [
      'Keyword research',
      'Product title optimization',
      'Search visibility enhancement',
      'Category optimization',
      'Product discoverability strategies',
      'Conversion-focused product content',
      'Competitive positioning',
    ],
  },
  {
    title: 'Promotional Campaign & Visibility Management',
    gradient: 'linear-gradient(135deg, #ea580c, #f59e0b)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Campaign participation planning',
      'Promotional visibility optimization',
      'Discount strategy management',
      'Product launch campaigns',
      'Seasonal promotions',
      'Featured product management',
      'Visibility enhancement strategies',
    ],
  },
  {
    title: 'Inventory & Dark Store Optimization',
    gradient: 'linear-gradient(135deg, #16a34a, #10b981)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    points: [
      'Inventory forecasting',
      'Replenishment planning',
      'Dark store inventory alignment',
      'Stock monitoring',
      'Fast-moving inventory optimization',
      'Availability management',
      'Inventory health analysis',
    ],
  },
  {
    title: 'Pricing & Profitability Optimization',
    gradient: 'linear-gradient(135deg, #db2777, #e879f9)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    points: [
      'Pricing strategy development',
      'Competitive pricing analysis',
      'Promotional pricing management',
      'Margin optimization',
      'Discount planning',
      'Market positioning strategies',
    ],
  },
  {
    title: 'Operational Performance Management',
    gradient: 'linear-gradient(135deg, #0891b2, #06b6d4)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Fulfillment performance monitoring',
      'Operational issue resolution',
      'Product availability tracking',
      'Seller compliance guidance',
      'Marketplace coordination',
      'Order performance optimization',
    ],
  },
  {
    title: 'Analytics & Reporting',
    gradient: 'linear-gradient(135deg, #475569, #334155)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    points: [
      'Sales reports',
      'Product performance analysis',
      'Inventory reports',
      'Profitability tracking',
      'Campaign performance reports',
      'Growth dashboards',
      'Operational analytics',
    ],
  },
];

const DIFFERENTIATORS = [
  'Dedicated marketplace specialists',
  'Quick commerce expertise',
  'Inventory-focused optimization',
  'Performance-driven growth strategies',
  'Transparent reporting',
  'Hyperlocal commerce understanding',
  'Scalable marketplace planning',
  'End-to-end account management',
];

const INDUSTRIES = [
  'FMCG',
  'Grocery Products',
  'Beauty & Personal Care',
  'Health & Wellness',
  'Packaged Foods',
  'Household Essentials',
  'Baby Care',
  'Pet Products',
  'Organic Products',
  'D2C Consumer Brands',
];

const STAGES = [
  'Launching a new brand on quick commerce',
  'Expanding existing product catalog',
  'Scaling promotional campaigns',
  'Improving inventory availability',
  'Optimizing operational performance',
  'Growing hyperlocal market presence',
];

const ADDITIONAL = [
  'Product launch management',
  'Hyperlocal visibility enhancement',
  'Seasonal campaign management',
  'Festival promotions',
  'Daily essentials promotions',
  'Brand growth & expansion',
  'SKU scaling strategies',
  'Long-term quick commerce growth planning',
];

const FAQS = [
  {
    q: 'Do you help with Blinkit seller onboarding?',
    a: 'Yes. We assist with seller registration, product approvals, operational readiness, account setup, and marketplace onboarding.',
  },
  {
    q: 'Can you optimize Blinkit product listings?',
    a: 'Yes. We optimize product titles, categories, attributes, SKU structures, discoverability, and catalog organization to improve visibility and conversions.',
  },
  {
    q: 'Do you support FMCG and grocery brands?',
    a: 'Yes. We specialize in FMCG, grocery, essentials, beauty, wellness, and consumer product marketplace optimization strategies tailored for Blinkit.',
  },
  {
    q: 'Can you manage promotional campaigns?',
    a: 'Yes. We help brands prepare for promotional campaigns, seasonal demand spikes, visibility optimization, and hyperlocal growth opportunities.',
  },
  {
    q: 'Do you provide reporting?',
    a: 'Yes. We provide detailed reports covering sales, inventory performance, profitability, operational metrics, and marketplace growth opportunities.',
  },
];

export default function BlinkitPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Blinkit Marketplace</span>
          </div>
        </nav>

        {/* HERO */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            <div className="sp-hero-tag">Blinkit Marketplace</div>
            <h1 className="sp-hero-h1">Blinkit Marketplace Management Services</h1>
            <p className="sp-hero-sub">Complete Blinkit Seller Account Management & Quick Commerce Growth Solutions</p>
            <p className="sp-hero-desc">Grow your brand on Blinkit with professional quick commerce marketplace management services designed to improve product visibility, increase conversions, optimize inventory movement, and scale revenue. From seller onboarding and catalog optimization to promotional campaign management, inventory planning, dark store readiness, and quick commerce growth strategies, we help brands build a profitable presence on Blinkit.</p>
            <div className="sp-hero-stats">
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Quick</span><span className="sp-hero-stat-lbl">Commerce Focus</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">FMCG</span><span className="sp-hero-stat-lbl">Category Specialists</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Full</span><span className="sp-hero-stat-lbl">Account Management</span></div>
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Audit</a>
              <a href="#services" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* PLATFORM OVERVIEW */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-sec-img-grid">
              <div>
                <div className="sp-label">Platform Overview</div>
                <h2 className="sp-h2">What is Blinkit Marketplace?</h2>
                <p className="sp-para">Blinkit is one of India's leading quick commerce platforms focused on delivering groceries, essentials, beauty products, personal care items, and daily-use products within minutes. Blinkit operates through a network of dark stores and hyperlocal fulfillment systems, enabling brands to access high-frequency consumers through fast-delivery commerce.</p>
                <p className="sp-para">Why Sell on Blinkit:</p>
                <ul className="sp-bullet-list">
                  {OVERVIEW_POINTS.map((pt, i) => (
                    <li key={i} className="sp-bullet-item"><span className="sp-dot" />{pt}</li>
                  ))}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img1} alt="Blinkit marketplace management" />
              </div>
            </div>
          </div>
        </section>

        {/* HOW WE HELP */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">How We Help Your Brand</div>
            <h2 className="sp-h2">End-to-End Blinkit Marketplace Management</h2>
            <p className="sp-para">Success on Blinkit requires more than listing products. Quick commerce growth depends on inventory availability, optimized product catalogs, pricing competitiveness, campaign participation, operational readiness, and rapid replenishment systems.</p>
            <h3 className="sp-h3">We Work as Your Dedicated Blinkit Growth Partner:</h3>
            <div className="sp-approach-grid">
              {APPROACH.map((item, i) => <div key={i} className="sp-approach-tag">{item}</div>)}
            </div>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Process</div>
            <h2 className="sp-h2">How We Work</h2>
            <div className="sp-process-list">
              {PROCESS.map((step, i) => (
                <div key={i} className="sp-process-step">
                  <div className="sp-process-left">
                    <div className="sp-process-num">{step.num}</div>
                    {i < PROCESS.length - 1 && <div className="sp-process-line" />}
                  </div>
                  <div className="sp-process-body">
                    <h3 className="sp-process-title">{step.title}</h3>
                    <p className="sp-process-intro">{step.intro}</p>
                    <ul className="sp-process-subs">
                      {step.points.map((pt, j) => <li key={j} className="sp-process-sub"><span className="sp-dot-sm" />{pt}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Services We Provide</div>
            <h2 className="sp-h2">Everything Your Blinkit Business Needs</h2>
            <div className="sp-cards">
              {SERVICES.map((s, i) => (
                <div key={i} className="sp-card">
                  <div className="sp-card-accent" style={{ background: s.gradient }} />
                  <div className="sp-card-icon" style={{ background: s.gradient }}>{s.icon}</div>
                  <h3 className="sp-card-title">{s.title}</h3>
                  <ul className="sp-card-list">
                    {s.points.map((pt, j) => <li key={j} className="sp-card-item"><span className="sp-dot-sm" />{pt}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our Blinkit Marketplace Services</div>
            <h2 className="sp-h2">Quick Commerce Experts Focused on Growth</h2>
            <p className="sp-para">We combine quick commerce strategy, catalog optimization, inventory planning, visibility enhancement, operational consulting, and performance analytics to help brands scale successfully on Blinkit.</p>
            <div className="sp-why-grid">
              <div>
                <h3 className="sp-h3">Benefits of Working With Us</h3>
                <ul className="sp-diff-list">
                  {DIFFERENTIATORS.map((d, i) => (
                    <li key={i} className="sp-diff-item"><span className="sp-check" />{d}</li>
                  ))}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img2} alt="Blinkit marketplace management approach" />
              </div>
            </div>
            <h3 className="sp-h3">Industries We Serve</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => <span key={i} className="sp-industry-chip">{ind}</span>)}
            </div>
          </div>
        </section>

        {/* BUSINESS STAGE */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <h2 className="sp-h2">Quick Commerce Growth Solutions for Every Business Stage</h2>
            <p className="sp-para">Whether you are:</p>
            <div className="sp-stage-grid">
              {STAGES.map((s, i) => <div key={i} className="sp-stage-item"><span className="sp-dot" />{s}</div>)}
            </div>
            <p className="sp-para">Our team helps create a structured quick commerce growth strategy tailored to your business goals.</p>
          </div>
        </section>

        {/* ADDITIONAL SERVICES */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Additional Growth Services</div>
            <div className="sp-additional-grid">
              {ADDITIONAL.map((item, i) => <div key={i} className="sp-additional-item"><span className="sp-check" />{item}</div>)}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggle(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {openFaq === i && <div className="sp-faq-body"><div className="sp-faq-a">{faq.a}</div></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="sp-cta">
          <div className="sp-cta-inner">
            <h2 className="sp-cta-h2">Ready to Grow on Blinkit?</h2>
            <p className="sp-cta-desc">Scale your brand with complete Blinkit Marketplace Management Services focused on quick commerce catalog optimization, inventory planning, product visibility, operational efficiency, and long-term marketplace growth.</p>
            <a href="/#contact" className="sp-btn-white">Get Free Audit</a>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
